import React from 'react'

import { endOfWeek, isPast, isWithinInterval, startOfWeek } from 'date-fns'
import { HiMiniCalendar, HiOutlineCheckCircle } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import type { RelevancyThreshold } from '@/types/Schedule'

interface MatchesNaicsProps {
  naics?: string
  className?: string
}

export const MatchesNaics: React.FC<MatchesNaicsProps> = ({
  naics,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'group flex items-center gap-x-[6px] text-xs bg-gradient-to-r from-green-600 to-emerald-500 text-white font-medium px-2 py-0.5 rounded-full flex items-center shadow-sm',
        className,
      )}
    >
      <HiOutlineCheckCircle size={12} className="flex-shrink-0" />
      <span
        className={twMerge('whitespace-nowrap', naics && 'group-hover:hidden')}
      >
        Matches NAICS
      </span>
      {naics && (
        <span className={twMerge('hidden group-hover:block whitespace-nowrap')}>
          NAICS: {naics}
        </span>
      )}
    </div>
  )
}

interface RelevanceProps {
  relevance: RelevancyThreshold
  className?: string
}

const RELEVANCE_LABEL: Record<RelevancyThreshold, string> = {
  high: 'Highly Relevant',
  medium: 'Potentially Relevant',
  low: 'Not Relevant',
}

export const Relevance: React.FC<RelevanceProps> = ({
  relevance,
  className,
}) => {
  let relevanceClassName = 'from-yellow-600 to-amber-500'
  switch (relevance) {
    case 'high':
      relevanceClassName =
        'from-green-600 to-emerald-500 dark:from-green-700 dark:to-emerald-600'
      break
    case 'medium':
      relevanceClassName = 'from-yellow-600 to-amber-500'
      break
    case 'low':
      relevanceClassName = 'from-red-600 to-rose-500 dark:to-rose-600'
      break
  }
  return (
    <div
      className={twMerge(
        'flex items-center gap-x-[6px] text-xs bg-gradient-to-r text-white font-medium px-2 py-0.5 rounded-full flex items-center shadow-sm',
        relevanceClassName,
        className,
      )}
    >
      <span className="whitespace-nowrap">{RELEVANCE_LABEL[relevance]}</span>
    </div>
  )
}

Relevance.displayName = 'Home.Relevance'

export const isDueThisWeek = (
  dueDate: Date,
  skipActiveCheck: boolean = false,
) => {
  const dueThisWeek = isWithinInterval(dueDate, {
    start: startOfWeek(new Date()),
    end: endOfWeek(new Date()),
  })
  const isActive = !isPast(dueDate)
  return dueThisWeek && (skipActiveCheck || isActive)
}

interface DueThisWeekProps {
  className?: string
}

export const DueThisWeek: React.FC<DueThisWeekProps> = ({ className }) => {
  return (
    <div
      className={twMerge(
        'flex items-center gap-x-[6px] text-xs bg-gradient-to-r from-red-600 to-rose-500 dark:to-rose-600 text-white font-medium px-2 py-0.5 rounded-full flex items-center shadow-sm',
        className,
      )}
    >
      <HiMiniCalendar size={12} className="flex-shrink-0" />
      <span className="whitespace-nowrap">Due this week</span>
    </div>
  )
}

DueThisWeek.displayName = 'Home.DueThisWeek'
